"use client"

import * as React from "react"
import { CheckIcon, ChevronRightIcon, CircleIcon } from "lucide-react"
import { DropdownMenu as DropdownMenuPrimitive } from "radix-ui"

import { cn } from "@/custolink-newdesign/lib/utils"

function DropdownMenu({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Root>) {
  return <DropdownMenuPrimitive.Root data-slot="dropdown-menu" {...props} />
}

function DropdownMenuPortal({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Portal>) {
  return (
    <DropdownMenuPrimitive.Portal data-slot="dropdown-menu-portal" {...props} />
  )
}

function DropdownMenuTrigger({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Trigger>) {
  return (
    <DropdownMenuPrimitive.Trigger
      data-slot="dropdown-menu-trigger"
      {...props}
    />
  )
}

function DropdownMenuContent({
  className,
  sideOffset = 4,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Content>) {
  return (
    <DropdownMenuPrimitive.Portal>
      <DropdownMenuPrimitive.Content
        data-slot="dropdown-menu-content"
        sideOffset={sideOffset}
        className={cn(
          "cl:z-50 cl:max-h-(--radix-dropdown-menu-content-available-height) cl:min-w-[8rem] cl:origin-(--radix-dropdown-menu-content-transform-origin) cl:overflow-x-hidden cl:overflow-y-auto cl:rounded-md cl:border cl:bg-popover cl:p-1 cl:text-popover-foreground cl:shadow-md cl:data-[side=bottom]:slide-in-from-top-2 cl:data-[side=left]:slide-in-from-right-2 cl:data-[side=right]:slide-in-from-left-2 cl:data-[side=top]:slide-in-from-bottom-2 cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=closed]:zoom-out-95 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0 cl:data-[state=open]:zoom-in-95",
          className
        )}
        {...props}
      />
    </DropdownMenuPrimitive.Portal>
  )
}

function DropdownMenuGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Group>) {
  return (
    <DropdownMenuPrimitive.Group data-slot="dropdown-menu-group" {...props} />
  )
}

function DropdownMenuItem({
  className,
  inset,
  variant = "default",
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Item> & {
  inset?: boolean
  variant?: "default" | "destructive"
}) {
  return (
    <DropdownMenuPrimitive.Item
      data-slot="dropdown-menu-item"
      data-inset={inset}
      data-variant={variant}
      className={cn(
        "cl:relative cl:flex cl:cursor-default cl:items-center cl:gap-2 cl:rounded-sm cl:px-2 cl:py-1.5 cl:text-sm cl:outline-hidden cl:select-none cl:focus:bg-accent cl:focus:text-accent-foreground cl:data-[disabled]:pointer-events-none cl:data-[disabled]:opacity-50 cl:data-[inset]:pl-8 cl:data-[variant=destructive]:text-destructive cl:data-[variant=destructive]:focus:bg-destructive/10 cl:data-[variant=destructive]:focus:text-destructive cl:dark:data-[variant=destructive]:focus:bg-destructive/20 cl:[&_svg]:pointer-events-none cl:[&_svg]:shrink-0 cl:[&_svg:not([class*='size-'])]:size-4 cl:[&_svg:not([class*='text-'])]:text-muted-foreground cl:data-[variant=destructive]:*:[svg]:text-destructive!",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuCheckboxItem({
  className,
  children,
  checked,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.CheckboxItem>) {
  return (
    <DropdownMenuPrimitive.CheckboxItem
      data-slot="dropdown-menu-checkbox-item"
      className={cn(
        "cl:relative cl:flex cl:cursor-default cl:items-center cl:gap-2 cl:rounded-sm cl:py-1.5 cl:pr-2 cl:pl-8 cl:text-sm cl:outline-hidden cl:select-none cl:focus:bg-accent cl:focus:text-accent-foreground cl:data-[disabled]:pointer-events-none cl:data-[disabled]:opacity-50 cl:[&_svg]:pointer-events-none cl:[&_svg]:shrink-0 cl:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      checked={checked}
      {...props}
    >
      <span className="cl:pointer-events-none cl:absolute cl:left-2 cl:flex cl:size-3.5 cl:items-center cl:justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CheckIcon className="cl:size-4" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.CheckboxItem>
  )
}

function DropdownMenuRadioGroup({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioGroup>) {
  return (
    <DropdownMenuPrimitive.RadioGroup
      data-slot="dropdown-menu-radio-group"
      {...props}
    />
  )
}

function DropdownMenuRadioItem({
  className,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.RadioItem>) {
  return (
    <DropdownMenuPrimitive.RadioItem
      data-slot="dropdown-menu-radio-item"
      className={cn(
        "cl:relative cl:flex cl:cursor-default cl:items-center cl:gap-2 cl:rounded-sm cl:py-1.5 cl:pr-2 cl:pl-8 cl:text-sm cl:outline-hidden cl:select-none cl:focus:bg-accent cl:focus:text-accent-foreground cl:data-[disabled]:pointer-events-none cl:data-[disabled]:opacity-50 cl:[&_svg]:pointer-events-none cl:[&_svg]:shrink-0 cl:[&_svg:not([class*='size-'])]:size-4",
        className
      )}
      {...props}
    >
      <span className="cl:pointer-events-none cl:absolute cl:left-2 cl:flex cl:size-3.5 cl:items-center cl:justify-center">
        <DropdownMenuPrimitive.ItemIndicator>
          <CircleIcon className="cl:size-2 cl:fill-current" />
        </DropdownMenuPrimitive.ItemIndicator>
      </span>
      {children}
    </DropdownMenuPrimitive.RadioItem>
  )
}

function DropdownMenuLabel({
  className,
  inset,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Label> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.Label
      data-slot="dropdown-menu-label"
      data-inset={inset}
      className={cn(
        "cl:px-2 cl:py-1.5 cl:text-sm cl:font-medium cl:data-[inset]:pl-8",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSeparator({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Separator>) {
  return (
    <DropdownMenuPrimitive.Separator
      data-slot="dropdown-menu-separator"
      className={cn("cl:-mx-1 cl:my-1 cl:h-px cl:bg-border", className)}
      {...props}
    />
  )
}

function DropdownMenuShortcut({
  className,
  ...props
}: React.ComponentProps<"span">) {
  return (
    <span
      data-slot="dropdown-menu-shortcut"
      className={cn(
        "cl:ml-auto cl:text-xs cl:tracking-widest cl:text-muted-foreground",
        className
      )}
      {...props}
    />
  )
}

function DropdownMenuSub({
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.Sub>) {
  return <DropdownMenuPrimitive.Sub data-slot="dropdown-menu-sub" {...props} />
}

function DropdownMenuSubTrigger({
  className,
  inset,
  children,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubTrigger> & {
  inset?: boolean
}) {
  return (
    <DropdownMenuPrimitive.SubTrigger
      data-slot="dropdown-menu-sub-trigger"
      data-inset={inset}
      className={cn(
        "cl:flex cl:cursor-default cl:items-center cl:gap-2 cl:rounded-sm cl:px-2 cl:py-1.5 cl:text-sm cl:outline-hidden cl:select-none cl:focus:bg-accent cl:focus:text-accent-foreground cl:data-[inset]:pl-8 cl:data-[state=open]:bg-accent cl:data-[state=open]:text-accent-foreground cl:[&_svg]:pointer-events-none cl:[&_svg]:shrink-0 cl:[&_svg:not([class*='size-'])]:size-4 cl:[&_svg:not([class*='text-'])]:text-muted-foreground",
        className
      )}
      {...props}
    >
      {children}
      <ChevronRightIcon className="cl:ml-auto cl:size-4" />
    </DropdownMenuPrimitive.SubTrigger>
  )
}

function DropdownMenuSubContent({
  className,
  ...props
}: React.ComponentProps<typeof DropdownMenuPrimitive.SubContent>) {
  return (
    <DropdownMenuPrimitive.SubContent
      data-slot="dropdown-menu-sub-content"
      className={cn(
        "cl:z-50 cl:min-w-[8rem] cl:origin-(--radix-dropdown-menu-content-transform-origin) cl:overflow-hidden cl:rounded-md cl:border cl:bg-popover cl:p-1 cl:text-popover-foreground cl:shadow-lg cl:data-[side=bottom]:slide-in-from-top-2 cl:data-[side=left]:slide-in-from-right-2 cl:data-[side=right]:slide-in-from-left-2 cl:data-[side=top]:slide-in-from-bottom-2 cl:data-[state=closed]:animate-out cl:data-[state=closed]:fade-out-0 cl:data-[state=closed]:zoom-out-95 cl:data-[state=open]:animate-in cl:data-[state=open]:fade-in-0 cl:data-[state=open]:zoom-in-95",
        className
      )}
      {...props}
    />
  )
}

export {
  DropdownMenu,
  DropdownMenuPortal,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuLabel,
  DropdownMenuItem,
  DropdownMenuCheckboxItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuShortcut,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
}
